import { computed, inject, Injectable } from '@angular/core';
import { BdShareableSelectService } from '../../select/global-search/shareable-select.service';
import { MenuItem } from 'primeng/api';

import { folderIdOf, isSharedList } from '../navigation/workspace-location';
import { BdWorkspaceNavigator } from '../navigation/workspace-navigator';
import { FolderItem, InlineNotice } from '../shareables-overview.models';

/**
 * The breadcrumb's crumbs navigate via `MenuItem.command` (no href), so PrimeNG
 * renders them without a pointer cursor — restore it, as the workspace tree's
 * breadcrumb does (BDA-5165).
 */
export const SHARED_FOLDER_BREADCRUMB_CSS = `
  :host ::ng-deep .p-breadcrumb-home-item .p-breadcrumb-item-link,
  :host ::ng-deep .p-breadcrumb-item:not(.p-disabled) .p-breadcrumb-item-link {
    cursor: pointer;
  }
`;

const LOAD_ERROR: InlineNotice = {
  severity: 'error',
  title: 'Der Ordner konnte nicht geladen werden',
  message:
    'Bitte versuchen Sie es in einem Moment erneut oder kehren Sie später zurück.',
};

/**
 * Browsing *into* a folder that was shared, without leaving the "Geteilt" list
 * it was shared in (BDA-6454) — the global "Geteilt" tab and a group's
 * "Geteilt" tab. The breadcrumb starts at "Geteilt", not at the grantor's
 * root, and the group the user came from stays selected.
 *
 * A view over {@link BdWorkspaceNavigator}: the open folder is the location's
 * `folderId`, loaded and pathed there. What remains here is the list's own
 * reading of it, plus the one input only the list has — which folders it
 * shares, where a shared folder's path starts.
 */
@Injectable()
export class BdSharedFolderNavigator {
  private readonly _navigator = inject(BdWorkspaceNavigator);
  private readonly _selectService = inject(BdShareableSelectService);

  /** Whether a folder is open — the list shows its contents instead of the shares. */
  public readonly isOpen = computed(() => {
    const location = this._navigator.location();
    return isSharedList(location) && folderIdOf(location) !== null;
  });
  public readonly items = computed<readonly FolderItem[]>(() =>
    this.isOpen() ? this._navigator.list().items : [],
  );
  public readonly loading = computed(
    () => this.isOpen() && this._navigator.list().status === 'loading',
  );
  public readonly notice = computed<InlineNotice | null>(() =>
    this.isOpen() && this._navigator.list().status === 'error'
      ? LOAD_ERROR
      : null,
  );

  /** The "Geteilt" crumb leads back to the list of shares. */
  public readonly breadcrumbHome: MenuItem = {
    label: 'Geteilt',
    icon: 'pi pi-share-alt',
    command: () => this.close(),
  };
  public readonly breadcrumbItems = computed<MenuItem[]>(() =>
    this._navigator.crumbs().map((entry, index) => ({
      label: entry.name,
      command: () => this._navigator.goTo(index),
    })),
  );

  /** Opens a folder row of the list on screen, one level deeper. */
  public open(item: FolderItem): void {
    this._navigator.openFolder({ id: item.id, name: item.name });
  }

  /** Back to the list of shares. */
  public close(): void {
    this._navigator.goTo(null);
  }

  /** Reports the folders the list shares, once its grants are known. */
  public setSharedFolderIds(ids: ReadonlySet<string>): void {
    this._navigator.setSharedFolderIds(ids);
  }

  /**
   * Rows inside a shared folder are not grants: nothing to revoke or re-share
   * there, so the menu is about opening them.
   */
  public menuItems(
    item: FolderItem,
    onOpen: (row: FolderItem) => void,
  ): MenuItem[] {
    return [
      {
        label: 'Öffnen',
        icon: 'pi pi-external-link',
        command: () => onOpen(item),
      },
      {
        label: 'In neuem Tab öffnen',
        icon: 'pi pi-window-maximize',
        command: () => this._selectService.openInNewTab(item.id, item.type),
      },
    ];
  }
}
